import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { ElementHelper } from '@alchemiesolns/elements';

/**
 * Standard valence electron counts by element group.
 * These represent the number of electrons in the outermost shell.
 * Used to compute lone pairs: lonePairs = (valenceElectrons - charge - bondOrderSum) / 2
 */
const VALENCE_ELECTRONS: Record<string, number> = {
  H: 1,
  C: 4, N: 5, O: 6, F: 7,
  Si: 4, P: 5, S: 6, Cl: 7,
  Br: 7, I: 7,
  Se: 6, As: 5, Te: 6,
};

function getValenceElectrons(symbol: string): number | undefined {
  if (VALENCE_ELECTRONS[symbol] !== undefined) {
    return VALENCE_ELECTRONS[symbol];
  }
  // Fallback to ElementHelper for less common elements
  const valence = ElementHelper.getValenceBySymbol(symbol);
  return valence > 0 ? valence : undefined;
}

/**
 * Infers lone pair counts for all atoms in a workspace based on valence rules.
 *
 * Formula: lonePairs = (valenceElectrons - formalCharge - bondOrderSum) / 2
 *
 * Skips atoms that already have lonePairs > 0 (preserves explicitly set values).
 * Skips atoms with unknown valence electron counts (e.g., metals).
 *
 * @param workspace - Workspace to process
 * @returns New workspace with lone pairs set on applicable atoms
 */
export function inferLonePairs(workspace: MolecularWorkspace): MolecularWorkspace {
  let ws = workspace;

  for (const atom of workspace.getAllAtoms()) {
    // Skip atoms that already have lone pairs set
    if (atom.lonePairs > 0) continue;

    const valenceElectrons = getValenceElectrons(atom.symbol);
    if (valenceElectrons === undefined) continue;

    // Sum bond orders connected to this atom
    let bondOrderSum = 0;
    for (const bond of workspace.getAllBonds()) {
      if (bond.a1.id === atom.id || bond.a2.id === atom.id) {
        bondOrderSum += bond.order;
      }
    }

    // lonePairs = (valenceElectrons - formalCharge - bondOrderSum) / 2
    const lonePairs = Math.max(0, Math.floor((valenceElectrons - atom.charge - bondOrderSum) / 2));

    if (lonePairs > 0) {
      ws = ws.updateAtomLonePairs(atom.id, lonePairs);
    }
  }

  return ws;
}
